import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { LayoutGrid } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePermissions } from "@/hooks/usePermissions";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { useComposerVoice } from "../agent/rail/useComposerVoice";
import { useWorkshopQuickCreate } from "../workshop/api/useWorkshopQuickCreate";
import {
  QUICK_CREATE_FEATURED,
  addPick,
  canAddPick,
  featuredItems,
  pickId,
  quickCreateRef,
  removePick,
} from "../workshop/quickCreate";
import { CreoCard, CreoCardSkeleton } from "../workshop/components/QuickCreateParts";
import WorkshopQuickCreateDetail from "../workshop/components/WorkshopQuickCreateDetail";
import WorkshopProjectPicker, { usePickableProjects } from "../workshop/components/WorkshopProjectPicker";
import WorkshopStartComposer, { type WorkshopBootstrapPayload } from "../workshop/components/WorkshopStartComposer";
import { WorkshopDeskContext, type WorkshopDeskApi } from "../workshop/components/workshopDesk";

/**
 * The command centre (Figma 11 `Command centre`): the composer, the project the
 * chat will live in, and four Creos (the Workshop new chat's row; All opens the
 * Creos page). Home owns no chat: a send (or a file)
 * goes to the Workshop with the start in router state, and the Workshop's own
 * start path opens the chat in that project (none yet: M7 names one).
 */

export type HomeStart =
  | { kind: "message"; projectId: string | null; payload: WorkshopBootstrapPayload }
  | { kind: "files"; projectId: string; files: File[] };

const WORKSHOP = "/dashboard/workshop";

function CommandCentre({ brandId, brandName }: { brandId: string; brandName: string }) {
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const { active, latest, isLoading: projectsLoading } = usePickableProjects();
  const quick = useWorkshopQuickCreate(brandId);
  const [text, setText] = useState("");
  const [pills, setPills] = useState<WorkshopQuickCreateRef[]>([]);
  const [focusSeq, setFocusSeq] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [chosen, setChosen] = useState<WorkshopQuickCreateItem | null>(null);
  const voice = useComposerVoice(brandId, (fn) => setText(fn));

  const project = active.find((p) => p.id === picked) ?? latest;
  const firstRun = !projectsLoading && active.length === 0;
  const items = quick.data?.items ?? [];
  const featured = featuredItems(items);
  const inPrompt = (item: WorkshopQuickCreateItem) => pills.some((p) => pickId(p) === pickId(item));
  // As on the Workshop's new chat: a card in the prompt comes out again; one
  // with no room left opens its pop-up, which says so.
  const togglePick = (item: WorkshopQuickCreateItem) => {
    if (inPrompt(item)) {
      setPills((prev) => removePick(prev, quickCreateRef(item)));
      return;
    }
    if (!canAddPick(pills, item)) {
      setChosen(item);
      return;
    }
    setPills((prev) => addPick(prev, item));
    setFocusSeq((n) => n + 1);
  };

  const start = async (payload: WorkshopBootstrapPayload) => {
    const homeStart: HomeStart = { kind: "message", projectId: firstRun ? null : (project?.id ?? null), payload };
    navigate(WORKSHOP, { state: { homeStart } });
  };

  return (
    <div className="home-command">
      <WorkshopStartComposer
        text={text}
        onTextChange={setText}
        placeholder={`Start anything for ${brandName || "this brand"}: a post, a month, a brief, a question.`}
        inputLabel="Start a chat"
        focusSeq={focusSeq}
        disabled={!canEdit}
        onStart={start}
        onAttachFiles={
          canEdit && project && !firstRun
            ? (files) => {
                const homeStart: HomeStart = { kind: "files", projectId: project.id, files };
                navigate(WORKSHOP, { state: { homeStart } });
              }
            : undefined
        }
        attachDisabled={firstRun}
        quickCreates={pills}
        onQuickCreatesChange={setPills}
        voice={voice}
        brandId={brandId}
      />
      <div className="home-tray">
        <WorkshopProjectPicker
          project={project}
          projects={active}
          onPick={setPicked}
          canCreate={false}
          loading={projectsLoading}
        />
        <p className="home-tray-hint">
          {projectsLoading
            ? null
            : firstRun
            ? "Your first chat starts a project; Creo names it from your message."
            : "A chat always lives in a project; Creo reads its instructions and files."}
        </p>
      </div>
      {quick.isError || (quick.data && featured.length === 0) ? null : (
        <section className="ws-qc-row home-creos" aria-label="Creos">
          <div className="ws-qc-row-head">
            <h2 className="ws-qc-row-title">Creos</h2>
            <span className="ws-qc-row-push" />
            {/* All opens every Creo on the Workshop's Creos page. */}
            <Link to={`${WORKSHOP}?view=creos`} className="ws-qc-all">
              <LayoutGrid aria-hidden="true" />
              {quick.data ? `All ${items.length}` : "All"}
            </Link>
          </div>
          <ul className="ws-qc-features">
            {quick.data
              ? featured.map((item) => (
                  <CreoCard
                    key={pickId(item)}
                    item={item}
                    variant="row"
                    picked={inPrompt(item)}
                    disabled={!canEdit}
                    onPick={() => togglePick(item)}
                    onInfo={() => {
                      setChosen(item);
                    }}
                  />
                ))
              : Array.from({ length: QUICK_CREATE_FEATURED }, (_, i) => <CreoCardSkeleton key={i} variant="row" />)}
          </ul>
        </section>
      )}
      <WorkshopQuickCreateDetail
        item={chosen}
        onClose={() => setChosen(null)}
        projectId={project?.id ?? null}
        canWrite={canEdit}
        isPhone={isPhone}
        added={chosen ? inPrompt(chosen) : false}
        full={chosen ? !inPrompt(chosen) && !canAddPick(pills, chosen) : false}
        onUse={(item, projectId) => {
          setChosen(null);
          if (projectId) setPicked(projectId);
          setPills((prev) => addPick(prev, item));
          setFocusSeq((n) => n + 1);
        }}
      />
    </div>
  );
}

/* The project picker reads the brand off a Workshop desk; Home lends it one
   whose moves all go to the Workshop. */
export default function HomeComposer({ brandId, brandName }: { brandId: string; brandName: string }) {
  const navigate = useNavigate();
  const desk = useMemo<WorkshopDeskApi>(
    () => ({
      brandId,
      brandName,
      activeProjectId: null,
      activeThreadId: null,
      pickedProjectId: null,
      setPickedProjectId: () => {},
      newChat: () => navigate(WORKSHOP),
      openProjects: () => navigate(`${WORKSHOP}?view=projects`),
      openCreos: () => navigate(`${WORKSHOP}?view=creos`),
      openProject: (id) => navigate(`${WORKSHOP}?projectId=${encodeURIComponent(id)}`),
      openThread: (id, thread) =>
        navigate(`${WORKSHOP}?projectId=${encodeURIComponent(id)}&threadId=${encodeURIComponent(thread)}`),
      openNewProject: () => navigate(`${WORKSHOP}?view=projects`),
      openSearch: () => navigate(WORKSHOP),
      startQuickCreate: () => navigate(WORKSHOP),
      renamingThreadId: null,
      startRename: () => {},
      endRename: () => {},
    }),
    [brandId, brandName, navigate],
  );
  return (
    <WorkshopDeskContext.Provider value={desk}>
      <CommandCentre brandId={brandId} brandName={brandName} />
    </WorkshopDeskContext.Provider>
  );
}
